import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ActionContainer,
  StartProgressingButton,
  CompleteButton
} from './styles';

import {
  updateShiftRequestStatus,
  selectUpdateShiftRequestStatusLoading,
  selectUpdateShiftRequestStatusSuccess,
  selectUpdateShiftRequestStatusError,
  selectUpdateShiftRequestStatusMessage,
  completeShiftRequest,
  selectCompleteShiftRequestLoading,
  selectCompleteShiftRequestSuccess,
  selectCompleteShiftRequestError,
  selectCompleteShiftRequestMessage,
  resetUpdateShiftRequestStatus,
  resetCompleteShiftRequest,
  resetCancelShiftRequest,
} from '../../../../modules/shift';
import AlertPopup from '../../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';

interface ShiftRequestActionProps {
  status: string;
  shiftRequestId: number;
}

const ShiftRequestActions: React.FC<ShiftRequestActionProps> = ({
  status,
  shiftRequestId
}) => {
  const dispatch = useDispatch<any>();

  // Status checks (case insensitive)
  const isRequested = status.toLowerCase() === 'requested';
  const isProgressing = status.toLowerCase() === 'progressing';

  // "Start Progressing" is available when status is "Requested"
  const canStartProgressing = isRequested;

  // "Complete Request" is available when status is "Progressing"
  const canComplete = isProgressing;

  const updateLoading = useSelector(selectUpdateShiftRequestStatusLoading);
  const updateSuccess = useSelector(selectUpdateShiftRequestStatusSuccess);
  const updateError = useSelector(selectUpdateShiftRequestStatusError);
  const updateMessage = useSelector(selectUpdateShiftRequestStatusMessage);

  const completeLoading = useSelector(selectCompleteShiftRequestLoading);
  const completeSuccess = useSelector(selectCompleteShiftRequestSuccess);
  const completeError = useSelector(selectCompleteShiftRequestError);
  const completeMessage = useSelector(selectCompleteShiftRequestMessage);

  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');

  const [openCompleteConfirmation, setOpenCompleteConfirmation] = useState(false);

  useEffect(() => {
    // Reset state on mount
    dispatch(resetUpdateShiftRequestStatus());
    dispatch(resetCompleteShiftRequest());
    dispatch(resetCancelShiftRequest());

    // Clean up on unmount
    return () => {
      dispatch(resetUpdateShiftRequestStatus());
      dispatch(resetCompleteShiftRequest());
      dispatch(resetCancelShiftRequest());
    };
  }, [dispatch]);

  const handleStartProgressing = () => {
    dispatch(updateShiftRequestStatus(shiftRequestId));
  };

  const handleCompleteRequest = () => {
    setOpenCompleteConfirmation(true);
  };

  const confirmCompleteRequest = () => {
    dispatch(completeShiftRequest(shiftRequestId));
    setOpenCompleteConfirmation(false);
  };

  useEffect(() => {
    if (updateSuccess) {
      setAlertMessage(updateMessage || 'Shift request status updated successfully');
      setAlertSeverity('success');
      setOpenAlert(true);
    }
  }, [updateSuccess, updateMessage]);

  useEffect(() => {
    if (updateError) {
      setAlertMessage(updateError || 'Failed to update shift request status');
      setAlertSeverity('error');
      setOpenAlert(true);
    }
  }, [updateError]);

  useEffect(() => {
    if (completeSuccess) {
      setAlertMessage(completeMessage || 'Shift request completed successfully');
      setAlertSeverity('success');
      setOpenAlert(true);
    }
  }, [completeSuccess, completeMessage]);

  useEffect(() => {
    if (completeError) {
      setAlertMessage(completeError || 'Failed to complete shift request');
      setAlertSeverity('error');
      setOpenAlert(true);
    }
  }, [completeError]);

  if (!canStartProgressing && !canComplete) return null;

  return (
    <ActionContainer>
      {canStartProgressing && (
        <StartProgressingButton
          onClick={handleStartProgressing}
          disabled={updateLoading || completeLoading}
        >
          {updateLoading ? 'Updating...' : 'Start Progressing'}
        </StartProgressingButton>
      )}

      {canComplete && (
        <CompleteButton
          onClick={handleCompleteRequest}
          disabled={updateLoading || completeLoading}
        >
          {completeLoading ? 'Completing...' : 'Complete Request'}
        </CompleteButton>
      )}

      <ConfirmationDialog
        open={openCompleteConfirmation}
        onClose={() => setOpenCompleteConfirmation(false)}
        onConfirm={confirmCompleteRequest}
        title="Confirm Completion"
        message="Are you sure you want to complete this shift request? This will create the shift."
      />

      <AlertPopup
        open={openAlert}
        message={alertMessage}
        severity={alertSeverity}
        onClose={() => setOpenAlert(false)}
      />
    </ActionContainer>
  );
};

export default ShiftRequestActions;
